// Contentful Blog Integration
// ===========================

export const CONTENTFUL_SPACE_ID = 'zn3nvfzaieqg'
export const CONTENTFUL_ACCESS_TOKEN =
  '9iy2JuC0rpWH1HqGPBlBjfgNTAZzfXq-nrbQGQtfVNw'

// Set to true to fetch from Contentful
const USE_CONTENTFUL = true

// Your Contentful Content Type ID (check in Content model)
const CONTENT_TYPE_ID = 'blogPost' // Updated to match your content type

// Types
export interface BlogArticle {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string // HTML content
  category: string
  readTime: string
  publishedDate: string
  featuredImage?: string
}

// Helper to convert rich text to HTML (simplified)
function richTextToHtml(richText: any): string {
  if (!richText || !richText.content) return ''

  return richText.content
    .map((node: any) => {
      if (node.nodeType === 'paragraph') {
        const text = node.content?.map((c: any) => c.value || '').join('') || ''
        return `<p>${text}</p>`
      }
      if (node.nodeType === 'heading-2') {
        const text = node.content?.map((c: any) => c.value || '').join('') || ''
        return `<h2>${text}</h2>`
      }
      if (node.nodeType === 'heading-3') {
        const text = node.content?.map((c: any) => c.value || '').join('') || ''
        return `<h3>${text}</h3>`
      }
      if (node.nodeType === 'unordered-list') {
        const items =
          node.content
            ?.map((li: any) => {
              const text =
                li.content?.[0]?.content
                  ?.map((c: any) => c.value || '')
                  .join('') || ''
              return `<li>${text}</li>`
            })
            .join('') || ''
        return `<ul>${items}</ul>`
      }
      if (node.nodeType === 'ordered-list') {
        const items =
          node.content
            ?.map((li: any) => {
              const text =
                li.content?.[0]?.content
                  ?.map((c: any) => c.value || '')
                  .join('') || ''
              return `<li>${text}</li>`
            })
            .join('') || ''
        return `<ol>${items}</ol>`
      }
      return ''
    })
    .join('')
}

// Helper to extract plain text from rich text (for excerpt)
function richTextToPlainText(richText: any): string {
  if (!richText || !richText.content) return ''

  const extractText = (nodes: any[]): string => {
    return nodes
      .map((node: any) => {
        if (node.value) return node.value
        if (node.content) return extractText(node.content)
        return ''
      })
      .join(' ')
  }

  return extractText(richText.content).slice(0, 200) + '...'
}

// Helper to generate slug from title
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

// Helper to estimate read time
function estimateReadTime(content: string): string {
  const wordCount = content.split(/\s+/).length
  const minutes = Math.ceil(wordCount / 200)
  return `${minutes} min`
}

// Mock data (matches Contentful structure exactly)
const MOCK_ARTICLES: BlogArticle[] = [
  {
    id: '1',
    slug: 'hausrat-fehler-vermeiden',
    title: 'Die 5 häufigsten Fehler bei der Hausratversicherung',
    excerpt:
      'Diese Stolperfallen solltest du kennen, bevor du eine Hausratversicherung abschließt. Wir zeigen dir, worauf es wirklich ankommt.',
    content: `
      <h2>1. Unterversicherung – der teuerste Fehler</h2>
      <p>Viele unterschätzen den Wert ihres Hausrats. Eine Faustregel: Rechne mit 650€ pro Quadratmeter Wohnfläche. Bei 80m² wären das 52.000€ Versicherungssumme.</p>
      
      <h2>2. Grobe Fahrlässigkeit nicht mitversichert</h2>
      <p>Du hast vergessen, das Fenster zu schließen und es wurde eingebrochen? Ohne den Baustein "grobe Fahrlässigkeit" zahlt die Versicherung möglicherweise nicht.</p>
      
      <h2>3. Fahrraddiebstahl vergessen</h2>
      <p>Fahrräder sind oft nur bis zu einem geringen Betrag mitversichert. Hast du ein teures E-Bike? Dann brauchst du eine Erweiterung.</p>
      
      <h2>4. Elementarschäden nicht eingeschlossen</h2>
      <p>Überschwemmung, Starkregen, Erdrutsch – diese Schäden sind standardmäßig NICHT versichert. Ein wichtiger Zusatzbaustein.</p>
      
      <h2>5. Zu hohe Selbstbeteiligung</h2>
      <p>Eine Selbstbeteiligung spart Beitrag, aber: Bei kleinen Schäden (Glasbruch, etc.) zahlst du dann selbst. Überlege, was sich für dich lohnt.</p>
    `,
    category: 'Tipps',
    readTime: '4 min',
    publishedDate: '2025-01-20',
  },
  {
    id: '2',
    slug: 'pkv-vs-gkv-vergleich',
    title: 'PKV vs. GKV: Der große Vergleich 2025',
    excerpt:
      'Private oder gesetzliche Krankenversicherung? Wir erklären die wichtigsten Unterschiede und für wen sich ein Wechsel lohnt.',
    content: `
      <h2>Wer kann in die PKV?</h2>
      <p>Angestellte mit einem Bruttoeinkommen über der Jahresarbeitsentgeltgrenze (2025: 69.300€), Selbstständige und Beamte können in die private Krankenversicherung wechseln.</p>
      
      <h2>Vorteile der PKV</h2>
      <ul>
        <li>Oft bessere Leistungen (Chefarzt, Einzelzimmer)</li>
        <li>Kürzere Wartezeiten auf Termine</li>
        <li>Beitrag unabhängig vom Einkommen</li>
        <li>Beitragsrückerstattung bei Leistungsfreiheit</li>
      </ul>
      
      <h2>Nachteile der PKV</h2>
      <ul>
        <li>Beiträge steigen im Alter</li>
        <li>Rückkehr in die GKV schwierig</li>
        <li>Kinder müssen separat versichert werden</li>
        <li>Vorerkrankungen können zu Risikozuschlägen führen</li>
      </ul>
      
      <h2>Unser Fazit</h2>
      <p>Die Entscheidung ist sehr individuell. Lass dich beraten, bevor du wechselst – ein Fehler kann teuer werden.</p>
    `,
    category: 'Vergleiche',
    readTime: '6 min',
    publishedDate: '2025-01-15',
  },
  {
    id: '3',
    slug: 'haftpflicht-grundlagen',
    title: 'Privathaftpflicht: Warum sie jeder braucht',
    excerpt:
      'Die wichtigste Versicherung überhaupt? Wir erklären, was die Privathaftpflicht abdeckt und worauf du achten solltest.',
    content: `
      <h2>Was deckt die Privathaftpflicht ab?</h2>
      <p>Die Privathaftpflichtversicherung schützt dich, wenn du anderen einen Schaden zufügst – ob Personen-, Sach- oder Vermögensschäden.</p>
      
      <h2>Beispiele aus dem Alltag</h2>
      <ul>
        <li>Du stößt versehentlich eine teure Vase um</li>
        <li>Dein Kind beschädigt das Auto des Nachbarn</li>
        <li>Du verursachst einen Wasserschaden in der Mietwohnung</li>
        <li>Beim Sport verletzt du jemanden unabsichtlich</li>
      </ul>
      
      <h2>Wichtige Leistungen</h2>
      <p>Achte auf: Mindestens 10 Mio. € Deckungssumme, Mietsachschäden, Schlüsselverlust, und Gefälligkeitsschäden.</p>
      
      <h2>Was kostet das?</h2>
      <p>Eine gute Privathaftpflicht gibt es schon ab 40-60€ pro Jahr. Das ist weniger als ein Streaming-Abo – aber im Ernstfall unbezahlbar.</p>
    `,
    category: 'Grundlagen',
    readTime: '3 min',
    publishedDate: '2025-01-10',
  },
  {
    id: '4',
    slug: 'kfz-versicherung-wechseln',
    title: 'KFZ-Versicherung wechseln: So sparst du richtig',
    excerpt:
      'Jedes Jahr im Herbst ist Wechselzeit. Wir zeigen dir, wie du beim KFZ-Versicherungswechsel bis zu 30% sparen kannst.',
    content: `
      <h2>Wann kann ich wechseln?</h2>
      <p>Die meisten KFZ-Versicherungen laufen zum 31.12. aus. Kündigungsfrist: 1 Monat, also bis spätestens 30. November kündigen.</p>
      
      <h2>Sonderkündigungsrecht</h2>
      <p>Du kannst auch außerhalb der Frist kündigen bei: Beitragserhöhung, Schadensfall, oder Fahrzeugwechsel.</p>
      
      <h2>Worauf beim Vergleich achten?</h2>
      <ul>
        <li>Deckungssumme (mind. 100 Mio. € empfohlen)</li>
        <li>Werkstattbindung ja/nein</li>
        <li>Mallorca-Police für Mietwagen</li>
        <li>Rabattschutz bei Unfällen</li>
      </ul>
      
      <h2>Unser Tipp</h2>
      <p>Vergleiche nicht nur den Preis! Eine günstige Versicherung, die im Schadensfall nicht zahlt, ist keine Ersparnis.</p>
    `,
    category: 'Tipps',
    readTime: '4 min',
    publishedDate: '2025-01-05',
  },
  {
    id: '5',
    slug: 'berufsunfaehigkeit-wichtig',
    title: 'Berufsunfähigkeitsversicherung: Unterschätztes Risiko',
    excerpt:
      'Jeder Vierte wird berufsunfähig. Trotzdem haben die wenigsten eine BU. Wir erklären, warum sie so wichtig ist.',
    content: `
      <h2>Das unterschätzte Risiko</h2>
      <p>Statistisch wird jeder vierte Arbeitnehmer vor der Rente berufsunfähig. Die häufigsten Ursachen: Psychische Erkrankungen, Rückenprobleme, Krebs.</p>
      
      <h2>Was zahlt der Staat?</h2>
      <p>Die gesetzliche Erwerbsminderungsrente reicht meist nicht zum Leben. Durchschnittlich gibt es nur etwa 900€ im Monat – wenn überhaupt.</p>
      
      <h2>Worauf achten?</h2>
      <ul>
        <li>Verzicht auf abstrakte Verweisung</li>
        <li>Nachversicherungsgarantie</li>
        <li>Rückwirkende Leistung</li>
        <li>Keine Meldefristen</li>
      </ul>
      
      <h2>Wann abschließen?</h2>
      <p>Je früher, desto besser. Als junger, gesunder Mensch bekommst du die besten Konditionen. Vorerkrankungen können später zu Ausschlüssen führen.</p>
    `,
    category: 'Grundlagen',
    readTime: '5 min',
    publishedDate: '2024-12-28',
  },
]

// Fetch articles from Contentful or return mock data
export async function fetchBlogArticles(): Promise<BlogArticle[]> {
  if (!USE_CONTENTFUL) {
    await new Promise((resolve) => setTimeout(resolve, 300))
    return MOCK_ARTICLES
  }

  try {
    // Fetch entries with linked assets
    const response = await fetch(
      `https://cdn.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/entries?access_token=${CONTENTFUL_ACCESS_TOKEN}&content_type=${CONTENT_TYPE_ID}&order=-fields.datettime&include=1`,
    )

    if (!response.ok) {
      console.error('Contentful response not ok:', response.status)
      throw new Error('Failed to fetch articles')
    }

    const data = await response.json()

    // Create a map of assets for image lookup
    const assets: Record<string, any> = {}
    if (data.includes?.Asset) {
      data.includes.Asset.forEach((asset: any) => {
        assets[asset.sys.id] = asset
      })
    }

    // Transform Contentful response to our BlogArticle type
    // Your fields: Entry title, BlogPic, FullText, datettime
    return data.items.map((item: any) => {
      const title =
        item.fields['Entry title'] || item.fields.title || 'Untitled'
      const fullText = item.fields.FullText || item.fields.fullText
      const htmlContent = richTextToHtml(fullText)
      const plainText = richTextToPlainText(fullText)

      // Get image URL from linked asset
      let featuredImage: string | undefined
      const blogPic = item.fields.BlogPic || item.fields.blogPic
      if (blogPic?.sys?.id && assets[blogPic.sys.id]) {
        featuredImage = 'https:' + assets[blogPic.sys.id].fields.file.url
      }

      return {
        id: item.sys.id,
        slug: generateSlug(title),
        title: title,
        excerpt: plainText,
        content: htmlContent,
        category: 'Artikel', // Default category since you don't have one
        readTime: estimateReadTime(plainText),
        publishedDate: item.fields.datettime || item.sys.createdAt,
        featuredImage: featuredImage,
      }
    })
  } catch (error) {
    console.error('Error fetching from Contentful:', error)
    return MOCK_ARTICLES
  }
}

// Fetch single article by slug
export async function fetchBlogArticleBySlug(
  slug: string,
): Promise<BlogArticle | null> {
  const articles = await fetchBlogArticles()
  return articles.find((article) => article.slug === slug) || null
}

// Get all categories
export function getCategories(articles: BlogArticle[]): string[] {
  const categories = new Set(articles.map((a) => a.category))
  return Array.from(categories).sort()
}

// Format date for display
export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
